import { useMemo } from 'react';
import { ENUM_UTILS, HIDDEN_PERP_MARKET_INDEXES } from '@drift/common';
import { MarketType } from '@drift-labs/sdk';
import StatusBar from 'src/components/primitives/StatusBar/StatusBar';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { cm } from 'src/utils/classNames';

type HiddenMarketBannerProps = {
	className?: string;
};

export default function HiddenMarketBanner({
	className,
}: HiddenMarketBannerProps) {
	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const marketIndex = selectedMarket?.marketIndex;
	const marketType = selectedMarket?.marketType;
	const marketSymbol = selectedMarket?.market?.symbol;

	const removalTimestamp =
		marketIndex !== undefined
			? HIDDEN_PERP_MARKET_INDEXES.get(marketIndex)
			: undefined;

	const isHiddenPerpMarket =
		removalTimestamp !== undefined &&
		marketType !== undefined &&
		ENUM_UTILS.match(marketType, MarketType.PERP);

	const isPastRemovalDate =
		removalTimestamp !== undefined && Date.now() / 1000 >= removalTimestamp;

	const removalDateString = useMemo(() => {
		if (removalTimestamp === undefined) return '';
		return new Date(removalTimestamp * 1000).toLocaleDateString('en-US', {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
		});
	}, [removalTimestamp]);

	const [hasDismissed, setHasDismissed] = useSyncLocalStorage(
		`hidden_market_banner_dismissed_${marketIndex}`,
		false
	);

	if (!isHiddenPerpMarket || hasDismissed) {
		return null;
	}

	const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setHasDismissed(true);
	};

	return (
		<StatusBar
			variant="custom"
			barClassName={cm(
				'rounded-xs bg-star-yellow-bg text-star-yellow [&>span]:text-left',
				className
			)}
			closeButtonClassName="text-star-yellow hover:text-star-yellow"
			onClose={handleClose}
		>
			{isPastRemovalDate
				? `The ${marketSymbol} market has been removed from the dropdown menu on the UI. Trading can still be done via the market's URL.`
				: `The ${marketSymbol} market will be removed from the dropdown menu on the UI on ${removalDateString}. Trading can still be done via the market's URL.`}
		</StatusBar>
	);
}
